import { Inject, Injectable, InjectionToken } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LOCAL_STORAGE_KEY } from '@core/configs';
import { UserSession } from '@core/models/interfaces';
import { IMenu } from '@core/models/interfaces/menu.interface';

export const LOCAL_STORAGE = new InjectionToken<Storage>('Local Storage', {
  providedIn: 'root',
  factory: () => localStorage,
});

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  jwtHelperService: JwtHelperService = new JwtHelperService();

  constructor(@Inject(LOCAL_STORAGE) private storage: Storage) { }

  getKeys(): string[] {
    return Object.keys(this.storage);
  }

  getItem<T = any>(key: string): T {
    const value = this.storage.getItem(key);
    if (!value) return value as any;
    try {
      return JSON.parse(value);
    } catch (e) {
      return value as unknown as T;
    }
  }

  setItem<T>(key: string, value: T): void {
    if (value == null) {
      this.removeItem(key);
      return;
    }
    this.storage.setItem(key, JSON.stringify(value));
  }

  removeItem(key: string): void {
    this.storage.removeItem(key);
  }

  trigger(key: string): void {
    this.setItem(key, true);
    this.removeItem(key);
  }

  clear(exclude: string[] = []) {
    this.getKeys().forEach(key => {
      if (!exclude.includes(key)) {
        this.removeItem(key);
      }
    });
  }

  hasKey(key: string): boolean {
    return this.getKeys().includes(key);
  }

  setUserSession(value: UserSession): void {
    this.setItem(LOCAL_STORAGE_KEY.userSession, value);
  }

  setMenu(value: IMenu): void {
    this.setItem(LOCAL_STORAGE_KEY.menu, value);
  }

  getAccessToken(): string | null {
    const userSession = this.getUserSession();
    return userSession ? userSession.accessToken : null;
  }

  getRefreshToken(): string | null {
    const userSession = this.getUserSession();
    return userSession ? userSession.refreshToken : null;
  }

  getUserSession(): UserSession {
    return this.getItem<UserSession>(LOCAL_STORAGE_KEY.userSession);
  }

  getFullName(): string {
    const { accessToken } = this.getUserSession();
    const { userInfo } = this.jwtHelperService.decodeToken(accessToken);
    return `${userInfo.lastName}${userInfo.firstName}`;
  }

  getRoleName() {
    return this.getItem(LOCAL_STORAGE_KEY.role);
  }
}
